<template>
    <CustomDialog v-model="visible" :defaultTitle="titlePopup" @confirm="saveAvailability" @cancel="closeDialog" minHeight="90%">
        <template #btn_toggle>
            <div class="ppv2-flex ppv2-justify-center" :style="{ marginBottom: normalizeSizeVertical(5, true) }">
                <div class="toggleBtn ppv2-inline-flex ppv2-rounded-[8px] ppv2-overflow-hidden">
                    <button
                        v-for="tab in tabs"
                        :key="tab.value"
                        @click="activeTab = tab.value"
                        :class="[
                            'ppv2-text-black ppv2-px-6 ppv2-py-2 ppv2-text-sm poppinsMedium ppv2-transition-all ppv2-duration-200 ppv2-focus:outline-none',
                            activeTab === tab.value ? ' toggleActiveBtn' : '',
                        ]"
                    >
                        {{ tab.label }}
                    </button>
                </div>
            </div>
        </template>
        <template #content>
            <div
                v-if="activeTab === 'saisie'"
                class="bgSaisie ppv2-rounded-xl ppv2-border ppv2-border-dashed ppv2-border-[#dcdcdc] ppv2-border-opacity-20"
                :style="{
                    paddingTop: normalizeSize(24, true),
                    paddingBottom: normalizeSize(24, true),
                    paddingLeft: normalizeSize(32, true),
                    paddingRight: normalizeSize(32, true),
                }"
            >
                <h2
                    class="poppinsLight ppv2-text-black"
                    :style="{ fontSize: normalizeFontSize(15), marginBottom: normalizeSizeVertical(28, true) }"
                >
                    {{ defaultTitle }}
                </h2>
                <div class="ppv2-px-3">
                    <!-- Dates -->
                    <div
                        class="ppv2-flex ppv2-justify-start ppv2-flex-wrap"
                        :style="{ marginBottom: normalizeSizeVertical(28, true), gap: normalizeSize(63) }"
                    >
                        <!-- Start Date -->
                        <div class="ppv2-w-[200px]">
                            <BaseDateInput
                                v-model="dateStart"
                                :min-date="minDate"
                                :default-value="minDate"
                                required
                                :placeholder="
                                    formattedDateStart || $t('resource_planning.weekRangeScheduling.period_date_placeholder')
                                "
                                :label="$t('resource_planning.weekRangeScheduling.date_begin_label')"
                                :customSizeLabel="12"
                                inputClass="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                            />
                            <p v-if="errors.dateStart" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-1">
                                {{ errors.dateStart }}
                            </p>
                        </div>

                        <!-- End Date -->
                        <div class="ppv2-w-[200px]">
                            <BaseDateInput
                                v-model="dateEnd"
                                :min-date="minDateEnd"
                                :default-value="dateStart"
                                :required="false"
                                :placeholder="
                                    formattedDateEnd || $t('resource_planning.weekRangeScheduling.period_date_placeholder')
                                "
                                :label="$t('resource_planning.weekRangeScheduling.date_end_label')"
                                :customSizeLabel="12"
                                inputClass="!ppv2-rounded-lg !ppv2-h-[30px] !ppv2-min-h-[30px]"
                            />
                            <p v-if="errors.dateEnd" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-1">{{ errors.dateEnd }}</p>
                        </div>
                    </div>

                    <!-- Jours spécifiques -->
                    <div class="ppv2-flex ppv2-flex-col" :style="{ marginBottom: normalizeSizeVertical(35, true) }">
                        <h3
                            class="poppinsMedium ppv2-text-black ppv2-text-xs"
                            :style="{ marginBottom: normalizeSizeVertical(16, true) }"
                        >
                            {{
                                mode === 'tarif'
                                    ? $t('experience.step5.specificMode.applicableDays')
                                    : $t('experience.step4.specificMode.specificDays')
                            }}
                        </h3>
                        <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-2">
                            <BaseCircleButton
                                v-for="day in days"
                                :key="day.id"
                                :active="selectedDays.includes(day.id)"
                                @click="toggleDay(day.id)"
                            >
                                {{ day.label }}
                            </BaseCircleButton>
                            <p v-if="errors.selectedDays" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-1">
                                {{ errors.selectedDays }}
                            </p>
                        </div>
                    </div>

                    <!-- Horaires -->
                    <div
                        class="ppv2-flex ppv2-justify-start ppv2-flex-wrap"
                        :style="{ marginBottom: normalizeSizeVertical(28, true), gap: normalizeSize(63) }"
                    >
                        <!-- Start Time -->
                        <div class="ppv2-w-[200px]">
                            <BaseTimeInput
                                v-model="timeStart"
                                :disabled="!dateStart"
                                required
                                :label="$t('resource_planning.scheduling_temporary_closures.hour_begin_label')"
                                :customSizeLabel="12"
                                inputClass="!ppv2-rounded-lg ppv2-min-h-[30px] ppv2-h-[30px]"
                            />
                            <p v-if="errors.timeStart" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-1">
                                {{ errors.timeStart }}
                            </p>
                        </div>

                        <!-- End Time -->
                        <div class="ppv2-w-[200px]">
                            <BaseTimeInput
                                v-model="timeEnd"
                                :min-time="timeStart"
                                :disabled="!dateStart"
                                required
                                :label="$t('resource_planning.scheduling_temporary_closures.hour_end_label')"
                                :customSizeLabel="12"
                                inputClass="!ppv2-rounded-lg ppv2-min-h-[30px] ppv2-h-[30px]"
                            />
                            <p v-if="errors.timeEnd" class="ppv2-text-[#ed4b9e] ppv2-text-xs ppv2-mt-1">{{ errors.timeEnd }}</p>
                        </div>
                    </div>

                    <!-- Prix (uniquement en mode tarif) -->
                    <div
                        v-if="mode === 'tarif'"
                        class="ppv2-flex ppv2-justify-start ppv2-flex-wrap"
                        :style="{ marginBottom: normalizeSizeVertical(28, true), gap: normalizeSize(63) }"
                    >
                        <div class="ppv2-w-[200px]">
                            <CustomInput
                                v-model="price"
                                :label="$t('experience.timeSpecific.priceTTCPerPerson')"
                                :required="false"
                                :state="getState()"
                                :right-icon="$symbolCurrency"
                                :errorText="errors.price"
                                type="number"
                                :min="1"
                                @input="clearError"
                                classInputAdd="!ppv2-rounded-lg ppv2-min-h-[30px] ppv2-h-[30px]"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                :customSizeLabel="12"
                                customClassLabel="poppinsMedium"
                            />
                        </div>
                    </div>
                    <div
                        v-if="mode === 'tarif'"
                        class="ppv2-flex ppv2-flex-col"
                        :style="{ marginBottom: normalizeSizeVertical(35, true) }"
                    >
                        <SelectableList
                            :label="$t('experience.timeSpecific.timeType')"
                            v-model="off_peak_time"
                            :options="options_horaire"
                            :withoutBorder="true"
                            :inSameLine="true"
                        />
                    </div>
                    <div
                        v-if="mode === 'tarif'"
                        class="ppv2-flex ppv2-flex-col"
                        :style="{ marginBottom: normalizeSizeVertical(35, true) }"
                    >
                        <SelectableList
                            :label="$t('experience.timeSpecific.allowMixing')"
                            v-model="authorize_mixing"
                            :options="options_mix_mode"
                            :withoutBorder="true"
                            :inSameLine="true"
                        />
                    </div>
                </div>
            </div>

            <div v-else-if="activeTab === 'calendrier'">
                <div v-if="errors.calendar" class="ppv2-px-8 ppv2-mb-4">
                    <p
                        class="ppv2-text-[#ed4b9e] ppv2-text-sm ppv2-bg-[#ed4b9e] ppv2-bg-opacity-10 ppv2-p-3 ppv2-rounded-lg ppv2-border ppv2-border-[#ed4b9e] ppv2-border-opacity-20"
                    >
                        {{ errors.calendar }}
                    </p>
                </div>
                <CalendarFull
                    ref="calendarFull"
                    :subCategoryId="subCategoryId"
                    :establishmentId="establishmentId"
                    :prestation="prestation"
                    :externalEvents="horaires_specifiques"
                    :externalCollisionCheck="checkOverlaps"
                    @save-rule="handleCalendarSave"
                    @checked-dates-change="onCalendarCheckedDatesChange"
                />
            </div>
        </template>

        <template #actions>
            <BaseButton
                v-if="activeTab === 'calendrier'"
                type="button"
                variant="outline"
                customClass="!ppv2-min-h-[40px]"
                :customHeight="43"
                @click="onCalendarCancelCheckedDates"
            >
                {{ $t('common.cancel') }}
            </BaseButton>
            <BaseButton
                v-if="activeTab === 'calendrier' && calendarCheckedDates.length > 0"
                type="button"
                variant="primary"
                customClass="!ppv2-min-h-[40px]"
                :customHeight="43"
                @click="onCalendarConfigureSpecificRates"
            >
                {{
                    mode === 'tarif'
                        ? $t('experience.step5.specificMode.titlePopup')
                        : $t('experience.step4.specificMode.calendar.typeRecurrence.once')
                }}
            </BaseButton>
            <BaseButton
                v-if="editingData && $listeners.delete"
                type="button"
                variant="outline"
                customClass="!ppv2-min-h-[40px] !ppv2-text-[#ed4b9e] !ppv2-border-[#ed4b9e]"
                :customHeight="43"
                @click="deleteAvailability"
            >
                {{ $t('common.delete') }}
            </BaseButton>
            <BaseButton
                id="button"
                type="button"
                variant="primary"
                customClass="!ppv2-min-h-[40px]"
                :customHeight="43"
                @click="saveAvailability"
            >
                {{ $t('common.save') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import CustomDialog from '../../../Shared/CustomDialog.vue'
import CalendarFull from '../../../Shared/CalendarFull.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import BaseCircleButton from '../../../Shared/BaseCircleButton.vue'
import BaseTimeInput from '../../../Shared/BaseTimeInput.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import SelectableList from '../../../Shared/SelectableList.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { SENTINEL_END_DATE } from '../../../../constants/planning'

export default {
    name: 'TimeSpecific',
    components: {
        CustomInput,
        BaseTimeInput,
        BaseCircleButton,
        BaseDateInput,
        CustomDialog,
        CalendarFull,
        SelectableList,
        BaseSwitch,
        BaseButton,
    },
    props: {
        editingIndex: {
            type: Number,
            default: -1,
        },
        defaultTitle: {
            type: String,
            default: '',
        },
        titlePopup: { type: String, default: '' },
        value: Boolean,
        mode: {
            type: String,
            default: 'disponibility', // 'disponibility' ou 'tarif'
            validator: (value) => ['disponibility', 'tarif'].includes(value),
        },
        editingData: {
            type: Object,
            default: null,
        },
        subCategoryId: {
            type: [Number, String],
            default: null,
        },
        establishmentId: {
            type: [Number, String],
            default: null,
        },
        prestation: {
            type: Object,
            default: null,
        },
        horaires_specifiques: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            activeTab: 'saisie',
            tabs: [
                { value: 'saisie', label: this.$t('experience.step4.specificMode.saisieMode') },
                { value: 'calendrier', label: this.$t('experience.step4.specificMode.calendarMode') },
            ],

            minDate: new Date(),
            visible: this.value,
            dateStart: null,
            dateEnd: null,
            timeStart: '08:00',
            timeEnd: '18:00',
            lunchStart: '13:00',
            lunchEnd: '13:30',
            isPeack: false,
            price: null,
            authorize_mixing: true,
            off_peak_time: true,
            days: [
                { id: 'Monday', label: this.$t('experience.timeSpecific.days.mon') },
                { id: 'Tuesday', label: this.$t('experience.timeSpecific.days.tue') },
                { id: 'Wednesday', label: this.$t('experience.timeSpecific.days.wed') },
                { id: 'Thursday', label: this.$t('experience.timeSpecific.days.thu') },
                { id: 'Friday', label: this.$t('experience.timeSpecific.days.fri') },
                { id: 'Saturday', label: this.$t('experience.timeSpecific.days.sat') },
                { id: 'Sunday', label: this.$t('experience.timeSpecific.days.sun') },
            ],
            selectedDays: [],
            errors: {
                dateStart: '',
                dateEnd: '',
                timeStart: '',
                timeEnd: '',
                lunchStart: '',
                lunchEnd: '',
                price: '',
                calendar: '',
            },
            options_mix_mode: [
                {
                    label: this.$t('experience.timeSpecific.forbidMixing'),
                    value: false,
                    subtitle: '',
                },
                {
                    label: this.$t('experience.timeSpecific.allowMixingOption'),
                    value: true,
                    subtitle: '',
                },
            ],
            options_horaire: [
                {
                    label: this.$t('experience.timeSpecific.peakHour'),
                    value: true,
                    subtitle: '',
                },
                {
                    label: this.$t('experience.timeSpecific.offPeakHour'),
                    value: false,
                    subtitle: '',
                },
            ],
            calendarCheckedDates: [],
        }
    },
    watch: {
        value(val) {
            this.visible = val
            if (val && this.editingData) {
                this.populateForm()
            }
        },
        visible(val) {
            this.$emit('input', val)
        },
    },
    methods: {
        normalizeFontSize,
        getState() {
            if (this.errors.price) return 'error'
            if (this.price) return 'valid'
            return 'default'
        },
        clearError() {
            if (this.errors.price) {
                this.errors.price = ''
            }
        },
        normalizeSizeVertical,
        normalizeSize,
        populateForm() {
            if (this.editingData) {
                this.dateStart = this.editingData.dateStart || null
                this.dateEnd = this.editingData.dateEnd || null
                this.timeStart = this.formatTime(this.editingData.dateStart + 'T' + this.editingData.timeStart)
                this.timeEnd = this.formatTime(this.editingData.dateStart + 'T' + this.editingData.timeEnd)
                this.lunchStart = this.editingData.lunchStart
                    ? this.formatTime(this.editingData.dateStart + 'T' + this.editingData.lunchStart)
                    : ''
                this.lunchEnd = this.editingData.lunchEnd
                    ? this.formatTime(this.editingData.dateStart + 'T' + this.editingData.lunchEnd)
                    : ''
                this.isPeack = this.lunchStart && this.lunchEnd && this.lunchStart !== this.lunchEnd ? true : false
                this.price = this.editingData.price || null
                this.authorize_mixing = this.editingData.authorize_mixing == 0 ? false : true
                this.off_peak_time = this.editingData.off_peak_time == 0 ? false : true
                // S'assurer que selectedDays est bien un tableau
                this.selectedDays = Array.isArray(this.editingData.selectedDays)
                    ? this.editingData.selectedDays
                          .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                          .filter(Boolean)
                    : this.editingData.selectedDays
                      ? this.editingData.selectedDays.split(',').map((d) => d.trim())
                      : []
            }
        },
        saveAvailability() {
            if (this.activeTab == 'saisie') {
                if (this.validateForm()) {
                    let finalSelectedDays = [...this.selectedDays]

                    // Treat "user cleared all days" as a delete intent ONLY when editing an EXISTING event
                    // in the event flow (parent wires @delete; an existing event carries a tmp_id).
                    // Everywhere else — new/checked-date creation, or the tarif/horaire lists (no @delete) —
                    // keep the legacy "empty selection = all days" default so their behaviour is unchanged.
                    if (
                        finalSelectedDays.length === 0 &&
                        !(this.editingData && this.editingData.tmp_id && this.$listeners.delete)
                    ) {
                        finalSelectedDays = this.days.map((day) => day.id)
                    }

                    const data = {
                        dateStart: this.dateStart,
                        dateEnd: this.dateEnd,
                        timeStart: this.timeStart,
                        timeEnd: this.timeEnd,
                        selectedDays: finalSelectedDays,
                        tmp_id: this.editingData?.tmp_id || Date.now(),
                    }

                    // Ajouter le prix seulement en mode tarif
                    if (this.mode === 'tarif') {
                        data.price = this.price
                        data.authorize_mixing = this.authorize_mixing
                        data.off_peak_time = this.off_peak_time
                    }
                    if (this.mode != 'tarif') {
                        data.isPeack = this.isPeack
                        data.lunchStart = this.isPeack && this.lunchStart ? this.lunchStart : null
                        data.lunchEnd = this.isPeack && this.lunchEnd ? this.lunchEnd : null
                    }

                    this.$emit('save', data)
                    this.visible = false
                    this.resetForm()
                }
            }
        },
        deleteAvailability() {
            this.$emit('delete')
            this.visible = false
            this.resetForm()
        },
        onCalendarCheckedDatesChange(dates) {
            this.calendarCheckedDates = Array.isArray(dates) ? dates : []
        },
        onCalendarCancelCheckedDates() {
            if (this.$refs.calendarFull && this.$refs.calendarFull.cancelCheckedDates) {
                this.$refs.calendarFull.cancelCheckedDates()
            }
        },
        onCalendarConfigureSpecificRates() {
            if (this.$refs.calendarFull && this.$refs.calendarFull.openCheckedDatesDialog) {
                this.$refs.calendarFull.openCheckedDatesDialog()
            }
        },
        validateForm() {
            this.errors = {
                dateStart: '',
                dateEnd: '',
                timeStart: '',
                timeEnd: '',
                lunchStart: '',
                lunchEnd: '',
                price: '',
                authorize_mixing: '',
                selectedDays: '',
            }

            if (!this.dateStart) this.errors.dateStart = this.$t('resource_planning.exceptions.validation.start_date_required')
            if (!this.timeStart) this.errors.timeStart = this.$t('resource_planning.exceptions.validation.start_heure_required')
            if (!this.timeEnd) this.errors.timeEnd = this.$t('resource_planning.exceptions.validation.end_heure_required')

            // Validation spécifique au mode tarif
            if (this.mode === 'tarif') {
                // if (!this.price) this.errors.price = this.$t('experience.errors.requiredPrice')
                if (this.price && this.price < 0) this.errors.price = this.$t('experience.errors.positivePrice')
            }

            if (this.dateStart && this.dateEnd && this.dateEnd < this.dateStart) {
                this.errors.dateEnd = this.$t('experience.errors.incompatibleDateEnd')
            }
            if (this.timeStart && this.timeEnd && this.timeEnd <= this.timeStart) {
                this.errors.timeEnd = this.$t('experience.errors.incompatibleTimeEnd')
            }
            if (this.mode !== 'tarif') {
                if (this.isPeack) {
                    if (!this.lunchStart) {
                        this.errors.lunchStart = this.$t('resource_planning.exceptions.validation.start_heure_required')
                    }

                    if (!this.lunchEnd) {
                        this.errors.lunchEnd = this.$t('resource_planning.exceptions.validation.end_heure_required')
                    }

                    if (this.lunchStart && this.lunchEnd && this.lunchEnd <= this.lunchStart) {
                        this.errors.lunchEnd = this.$t('experience.errors.incompatibleTimeEnd')
                    }

                    // Pause must be inside main time range
                    if (this.timeStart && this.lunchStart && this.lunchStart < this.timeStart) {
                        this.errors.lunchStart = this.$t('experience.errors.incompatibleTimeStart')
                    }

                    if (this.timeEnd && this.lunchEnd && this.lunchEnd > this.timeEnd) {
                        this.errors.lunchEnd = this.$t('experience.errors.incompatibleTimeEnd')
                    }
                }
            }
            if (this.checkOverlaps()) {
                this.errors.dateStart =
                    this.$t('experience.errors.overlapDisponibility') || 'Collision détectée avec un autre horaire.'
            }
            return !Object.values(this.errors).some((error) => error !== '')
        },
        checkOverlaps(data = null) {
            // Use provided data or local state
            const target = data || {
                dateStart: this.dateStart,
                dateEnd: this.dateEnd,
                timeStart: this.timeStart,
                timeEnd: this.timeEnd,
                selectedDays: this.selectedDays,
            }

            for (let i = 0; i < this.horaires_specifiques.length; i++) {
                if (i === this.editingIndex) continue

                const existing = this.horaires_specifiques[i]

                // Normaliser selectedDays pour existing
                let existingDays = []
                if (Array.isArray(existing.selectedDays)) {
                    existingDays = existing.selectedDays
                        .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                        .filter(Boolean)
                } else if (typeof existing.selectedDays === 'string') {
                    existingDays = existing.selectedDays.split(',').map((d) => d.trim())
                }

                // Normaliser selectedDays pour target
                let targetDays = []
                if (Array.isArray(target.selectedDays)) {
                    targetDays = target.selectedDays
                        .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                        .filter(Boolean)
                } else if (typeof target.selectedDays === 'string') {
                    targetDays = target.selectedDays.split(',').map((d) => d.trim())
                }

                // 1. Check Days Intersection
                const commonDays = existingDays.filter((day) => targetDays.includes(day))
                if (commonDays.length === 0) continue

                // 2. Check Date Range Overlap (treat missing dateEnd as far-future)
                const targetEnd = target.dateEnd || SENTINEL_END_DATE
                const existingEnd = existing.dateEnd || SENTINEL_END_DATE
                if (target.dateStart <= existingEnd && existing.dateStart <= targetEnd) {
                    // 3. Check Time Range Overlap
                    // (StartA < EndB) and (StartB < EndA)
                    if (target.timeStart < existing.timeEnd && existing.timeStart < target.timeEnd) {
                        return true // Overlap detected
                    }
                }
            }
            return false
        },
        handleCalendarSave(data) {
            const formattedData = {
                dateStart: data.dateStart,
                dateEnd: data.dateEnd,
                timeStart: data.timeStart,
                timeEnd: data.timeEnd,
                selectedDays: data.selectedDays,
                tmp_id: Date.now() + Math.random(), // Unique ID for each
            }

            if (this.mode === 'tarif') {
                formattedData.price = this.price || 0
                formattedData.authorize_mixing = this.authorize_mixing
                formattedData.off_peak_time = this.off_peak_time
            } else {
                formattedData.lunchStart = data.lunchStart || '13:00'
                formattedData.lunchEnd = data.lunchEnd || '13:30'
                formattedData.isPeack = data.isPeack || false
            }

            // Final safety check before emitting save
            if (this.checkOverlaps(formattedData)) {
                this.errors.calendar =
                    this.$t('experience.errors.overlapDisponibility') || 'Collision détectée avec un autre horaire.'
                console.warn('⚠️ Collision detected in Calendar mode, skipping save.', formattedData)
                return
            }

            this.errors.calendar = ''
            this.$emit('save', formattedData)

            // We don't close immediately here because monthly recurrence emits multiple times
            // The calendar confirmDialog handles closing its own internal dialog
            // But we want to close the TimeSpecific dialog after saving from calendar
            // To avoid closing multiple times, we can use a small timeout or check if it's already closing
            if (this.visible) {
                setTimeout(() => {
                    this.visible = false
                    this.resetForm()
                }, 100)
            }
        },
        toggleDay(dayId) {
            const index = this.selectedDays.indexOf(dayId)
            if (index === -1) this.selectedDays.push(dayId)
            else this.selectedDays.splice(index, 1)
        },
        closeDialog() {
            this.visible = false
            this.resetForm()
            this.$emit('cancel')
        },
        resetForm() {
            this.activeTab = 'saisie'
            this.dateStart = null
            this.dateEnd = null
            this.timeStart = '08:00'
            this.timeEnd = '18:00'
            this.lunchStart = '13:00'
            this.lunchEnd = '13:30'
            this.price = null
            this.selectedDays = []
            this.calendarCheckedDates = []
            this.errors = {}
        },
        formatTime(datetime) {
            const d = new Date(datetime)
            return d.toTimeString().slice(0, 5) // "HH:mm"
        },
    },
    computed: {
        formattedDateStart: {
            get() {
                if (!this.dateStart) return ''
                const [year, month, day] = this.dateStart.split('-')
                return `${day}-${month}-${year}`
            },
            set(value) {
                const [day, month, year] = value.split('-')
                this.dateStart = `${year}-${month}-${day}`
            },
        },
        formattedDateEnd: {
            get() {
                if (!this.dateEnd) return ''
                const [year, month, day] = this.dateEnd.split('-')
                return `${day}-${month}-${year}`
            },
            set(value) {
                const [day, month, year] = value.split('-')
                this.dateEnd = `${year}-${month}-${day}`
            },
        },
        minDateEnd() {
            if (!this.dateStart) return this.minDate || null
            return this.dateStart
        },
    },
}
</script>

<style scoped>
/* Vos styles existants restent les mêmes */
.rounded-button {
    width: 34px;
    height: 34px;
    border-radius: 25px;
    border: 1px solid #5b5b9c;
    background: #191755;
}
.block_center {
    display: block;
}
.bgSaisie {
    background-color: rgba(0, 0, 0, 0.03);
}
.dark .bgSaisie {
    background-color: #191755;
}
.toggleBtn {
    background-color: rgba(0, 0, 0, 0.05);
}
.dark .toggleBtn {
    background-color: rgba(255, 255, 255, 0.04);
}
.toggleActiveBtn {
    background-color: rgba(0, 0, 0, 0.07);
}
.dark .toggleActiveBtn {
    background-color: rgba(255, 255, 255, 0.07);
}
</style>
